import React, { useState } from "react";
import { toast } from "react-toastify";

import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CircularProgress, IconButton, TableRow as MuiTableRow, Stack, TableCell } from "@mui/material";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { Menu } from "components/popups";
import { BalanceApproval } from "components/Prompts";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { PaymentStatus } from "constants/debt";
import { useActionHook, useFormatDateHook, useFormatNumericHook, usePrompt } from "hooks";
import { useDebtActions } from "store";
import { AmortizationColumn, ExpectedPaymentRow } from "store/types";

interface Props {
  row: ExpectedPaymentRow;
  columns: AmortizationColumn[];
  index: number;
  level?: number;
  onConfirmPayment: (row: ExpectedPaymentRow) => void;
  onActualPayment: (row: ExpectedPaymentRow) => void;
}

export const TableRow: React.FC<Props> = ({ row, columns, index, level = 0, onConfirmPayment, onActualPayment }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const dateFormat = useFormatDateHook();
  const formatNumeric = useFormatNumericHook();
  const openPrompt = usePrompt();

  const { getBalanceApprovalAction } = useDebtActions();
  const { action: getBalanceApproval, loading: getBalanceApprovalLoading } = useActionHook(getBalanceApprovalAction);

  const hasChildren = row.children && row.children.length > 0;

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleBalanceApproval = async () => {
    const res = await openPrompt({
      Component: BalanceApproval
    });

    if (!res) return;

    try {
      await getBalanceApproval({
        dealId: row.deal_id ?? 0,
        type: res.balance.toLowerCase(),
        ...(res.date && { report_date: res.date.toISOString() })
      });
    } catch (error: any) {
      toast.error(error?.message ?? "An error occurred");
    }
  };

  const handleConfirmPayment = () => {
    onConfirmPayment(row);
  };

  const handleActualPayment = () => {
    onActualPayment(row);
  };

  const getRowBackgroundColor = () => {
    if (row.status === PaymentStatus.RESOLVED) {
      return "#d4edda"; // Light green
    }
    if (row.status === PaymentStatus.MISMATCH) {
      return "#f8d7da"; // Light red
    }
    if (level > 0) {
      return `rgba(38, 166, 174, ${0.05 + level * 0.02})`;
    }
    return "transparent";
  };

  return (
    <>
      <MuiTableRow
        sx={{
          bgcolor: getRowBackgroundColor(),
          "&:hover": {
            bgcolor:
              row.status === PaymentStatus.RESOLVED
                ? "#c3e6cb"
                : row.status === PaymentStatus.MISMATCH
                  ? "#f5c6cb"
                  : "#ECF1F8"
          }
        }}
      >
        <TableCell padding="checkbox">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ ".MuiCheckbox-root": { padding: "0px !important" } }}
          >
            {/* <Checkbox /> */}
            {hasChildren && (
              <IconButton
                size="sm"
                onClick={handleToggleExpanded}
                sx={{
                  transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s",
                  padding: "2px"
                }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: "16px" }} />
              </IconButton>
            )}
          </Stack>
        </TableCell>
        <TableCell sx={{ width: "40px", padding: "6px", textAlign: "center", verticalAlign: "middle" }}>
          {row.status === PaymentStatus.RESOLVED && (
            <CheckIcon sx={{ fontSize: 18, color: "#28a745", display: "block", margin: "0 auto" }} />
          )}
          {row.status === PaymentStatus.MISMATCH && (
            <MaterialTooltip isShow title="Payment has arrear">
              <ErrorIcon sx={{ fontSize: 18, color: "#dc3545", display: "block", margin: "0 auto" }} />
            </MaterialTooltip>
          )}
        </TableCell>
        <TableCell>
          <NavigateLink
            allowNewTab={true}
            route="dealAmortization"
            params={{ deal_id: String(row.deal_id) }}
            style={{ color: "#26a6ae" }}
            disableLink={!row.deal_id}
          >
            {row.deal}
          </NavigateLink>
        </TableCell>
        <TableCell>
          <NavigateLink
            allowNewTab={true}
            route="dealAmortization"
            params={{ deal_id: String(row.sub_deal_id) }}
            style={{ color: "#26a6ae" }}
            disableLink={!row.sub_deal_id}
          >
            {row.sub_deal}
          </NavigateLink>
        </TableCell>
        <TableCell>{dateFormat({ date: row.date })}</TableCell>
        {columns.map((column) => (
          <TableCell key={column.key}>
            {formatNumeric({ value: (row[column.key as keyof ExpectedPaymentRow] as number) ?? 0, format: {} })}
          </TableCell>
        ))}
        <TableCell>{formatNumeric({ value: row.total_payment })}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            {getBalanceApprovalLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Menu
                iconButtonProps={{ size: "sm" }}
                options={[
                  ...(!hasChildren && row.status !== PaymentStatus.RESOLVED
                    ? [
                        { title: "Confirm payment", onClick: handleConfirmPayment },
                        { title: "Apply changed payment", onClick: handleActualPayment }
                      ]
                    : []),
                  { title: "Balance Confirmation", onClick: handleBalanceApproval }
                ]}
              />
            )}
          </Stack>
        </TableCell>
      </MuiTableRow>

      {/* Render children recursively */}
      {hasChildren &&
        isExpanded &&
        row.children!.map((child, childIndex) => (
          <TableRow
            key={`${index}-${childIndex}`}
            row={child}
            index={childIndex}
            level={level + 1}
            columns={columns}
            onConfirmPayment={onConfirmPayment}
            onActualPayment={onActualPayment}
          />
        ))}
    </>
  );
};
